import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://r5id.com.br';
const SITE_NAME = 'R5 Inteligência Digital';
const DEFAULT_IMAGE = `${SITE_URL}/seo-share-r5id.png`;
const DEFAULT_OG_IMAGE_WIDTH = '1200';
const DEFAULT_OG_IMAGE_HEIGHT = '630';
const DEFAULT_TITLE = 'R5 Inteligência Digital';
const DEFAULT_DESCRIPTION = 'Portal da R5 Inteligência Digital: inovação, IA, transformação digital, conteúdos, notícias e contato.';
const DEFAULT_KEYWORDS = [
  'R5 Inteligência Digital',
  'transformação digital',
  'inteligência artificial',
  'dados e IA',
  'inovação aplicada',
  'consultoria em tecnologia',
  'Brasília',
  'São Paulo',
  'Brasil'
];
const PROFESSIONAL_SERVICE_AREAS = [
  { '@type': 'Country', name: 'Brasil' },
  { '@type': 'AdministrativeArea', name: 'Distrito Federal' },
  { '@type': 'AdministrativeArea', name: 'São Paulo' }
];
const SAME_AS_URLS = [
  'https://www.linkedin.com/company/103669965/',
  'https://www.instagram.com/r5inteligenciadigital/'
];
const CORE_SERVICE_TOPICS = [
  'transformação digital',
  'inteligência artificial',
  'dados e analytics',
  'produtos digitais',
  'cloud e devops',
  'pesquisa e inovação'
];
const CONTENT_CHANNEL = process.env.REACT_APP_CHANNEL || 'desktop';

const normalizeApiUrl = (input) => {
  const fallback = '/api';
  const value = (input ?? '').toString().trim();
  if (!value) return fallback;

  if (value.startsWith('http://') || value.startsWith('https://')) {
    return value.replace(/\/+$/, '');
  }

  const withSlash = value.startsWith('/') ? value : `/${value}`;
  return withSlash.replace(/\/+$/, '');
};

const API_URL = normalizeApiUrl(process.env.REACT_APP_API_URL);

const stripHtml = (value) => String(value || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const truncate = (value, max = 160) => {
  const normalized = String(value || '').trim();
  if (!normalized) return '';
  if (normalized.length <= max) return normalized;
  return `${normalized.slice(0, max - 1).trim()}…`;
};

const firstGalleryImage = (gallery) => {
  if (!Array.isArray(gallery) || gallery.length === 0) return null;
  const first = gallery[0];
  if (typeof first === 'string') return first;
  if (first && typeof first === 'object') {
    return first.original || first.url || first.src || null;
  }
  return null;
};

const normalizeAbsoluteUrl = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized) return null;
  if (normalized.startsWith('http://') || normalized.startsWith('https://')) return normalized;
  if (normalized.startsWith('/')) return `${SITE_URL}${normalized}`;
  return `${SITE_URL}/${normalized.replace(/^\/+/, '')}`;
};

const buildKeywordString = (...parts) => {
  const flattened = parts.flatMap((part) => Array.isArray(part) ? part : [part]);
  const normalized = flattened
    .map((item) => String(item || '').trim())
    .filter(Boolean);
  return Array.from(new Set(normalized)).join(', ');
};

const keywordListFromText = (...parts) => buildKeywordString(
  DEFAULT_KEYWORDS,
  parts
    .flatMap((part) => String(part || '').split(/[\s,|:/-]+/))
    .map((token) => token.trim())
    .filter((token) => token.length >= 3)
);

const upsertMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    Object.entries(attrs.selectorAttrs || {}).forEach(([key, value]) => {
      el.setAttribute(key, value);
    });
    document.head.appendChild(el);
  }

  Object.entries(attrs.values || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      el.setAttribute(key, value);
    }
  });
};

const upsertLink = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('link');
    Object.entries(attrs.selectorAttrs || {}).forEach(([key, value]) => {
      el.setAttribute(key, value);
    });
    document.head.appendChild(el);
  }

  Object.entries(attrs.values || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      el.setAttribute(key, value);
    }
  });
};

const upsertJsonLd = (id, payload) => {
  let el = document.head.querySelector(`#${id}`);
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(payload);
};

const removeJsonLd = (id) => {
  const el = document.head.querySelector(`#${id}`);
  if (el) el.remove();
};

const resolveSeoByPath = (pathname) => {
  const routes = [
    {
      match: (p) => p === '/',
      title: 'R5 Inteligência Digital',
      description: 'Soluções em transformação digital, inteligência artificial, dados e inovação aplicada para organizações em Brasília, São Paulo e em todo o Brasil.',
      keywords: ['transformação digital no Brasil', 'IA para organizações', 'consultoria em inovação', 'Brasília', 'São Paulo'],
      schemaType: 'WebPage'
    },
    {
      match: (p) => p === '/news',
      title: 'Notícias e Conteúdos | R5 Inteligência Digital',
      description: 'Acompanhe notícias, artigos e conteúdos sobre inovação, inteligência artificial, transformação digital e pesquisa aplicada na R5.',
      keywords: ['notícias sobre IA', 'conteúdo de transformação digital', 'artigos de inovação'],
      schemaType: 'CollectionPage'
    },
    {
      match: (p) => p.startsWith('/news/'),
      title: 'Notícia | R5 Inteligência Digital',
      description: 'Leia notícias e análises da R5 sobre inovação, tecnologia, inteligência artificial e transformação digital.',
      keywords: ['notícia de inovação', 'inteligência artificial', 'transformação digital'],
      schemaType: 'Article'
    },
    {
      match: (p) => p === '/visao-geral' || p === '/nossa-historia',
      title: 'Visão Geral | R5 Inteligência Digital',
      description: 'Conheça a proposta de valor da R5 em transformação digital, dados, IA e inovação para organizações em Brasília, São Paulo e no Brasil.',
      keywords: ['visão geral R5', 'dados e IA', 'transformação digital em São Paulo', 'inovação em Brasília'],
      schemaType: 'AboutPage',
      serviceType: 'Consultoria em transformação digital, dados e inteligência artificial'
    },
    {
      match: (p) => p === '/proposito',
      title: 'Propósito | R5 Inteligência Digital',
      description: 'Entenda como a R5 conecta estratégia, tecnologia, dados e impacto real em projetos de inovação e transformação digital.',
      keywords: ['propósito R5', 'estratégia digital', 'impacto com tecnologia'],
      schemaType: 'AboutPage',
      serviceType: 'Estratégia digital e inovação aplicada'
    },
    {
      match: (p) => p === '/fale-conosco',
      title: 'Fale Conosco | R5 Inteligência Digital',
      description: 'Entre em contato com a R5 para projetos, parcerias e soluções personalizadas em IA, dados, transformação digital e inovação aplicada.',
      keywords: ['contato R5', 'projetos de IA', 'parcerias em transformação digital', 'consultoria em tecnologia'],
      schemaType: 'ContactPage',
      serviceType: 'Atendimento comercial para projetos de tecnologia e inovação'
    },
    {
      match: (p) => p === '/trabalhe-conosco',
      title: 'Trabalhe Conosco | R5 Inteligência Digital',
      description: 'Conheça as oportunidades da R5 em Gestão de Pessoas e RH e acompanhe as vagas disponíveis.',
      keywords: ['trabalhe conosco R5', 'carreiras R5', 'vagas RH', 'gestão de pessoas'],
      schemaType: 'CollectionPage',
      serviceType: 'Oportunidades em gestão de pessoas e RH'
    },
    {
      match: (p) => p === '/governanca' || p.startsWith('/governanca/'),
      title: 'Governança e Políticas | R5 Inteligência Digital',
      description: 'Acesse termos, políticas, documentos institucionais e materiais de governança da R5 Inteligência Digital.',
      keywords: ['governança R5', 'políticas institucionais', 'compliance e integridade'],
      schemaType: 'WebPage'
    },
    {
      match: (p) => p === '/parceiros' || p.startsWith('/parceiros/'),
      title: 'Parceiros | R5 Inteligência Digital',
      description: 'Conheça as alianças e parceiros estratégicos da R5 para acelerar inovação, pesquisa aplicada e transformação digital.',
      keywords: ['parcerias estratégicas', 'ecossistema de inovação', 'alianças em tecnologia'],
      schemaType: 'WebPage'
    },
    {
      match: (p) => p === '/clientes',
      title: 'Clientes | R5 Inteligência Digital',
      description: 'Veja organizações e iniciativas que confiam na R5 em projetos de dados, IA, pesquisa aplicada e transformação digital.',
      keywords: ['clientes R5', 'casos de transformação digital', 'projetos de IA'],
      schemaType: 'WebPage'
    },
    {
      match: (p) => p === '/insights/healthcare-payers',
      title: 'Healthcare Payers | Executive Briefing | R5 Inteligência Digital',
      description: 'Executive briefing da R5 sobre tecnologia, dados, IA e sistemas inteligentes para operadoras de saúde.',
      keywords: ['healthcare payers', 'operadoras de saúde', 'executive briefing', 'dados e IA em saúde', 'sistemas inteligentes em saúde'],
      schemaType: 'WebPage',
      serviceType: 'Executive briefing sobre tecnologia para operadoras de saúde'
    },
    {
      match: (p) => p === '/insights/hospitals',
      title: 'Hospitals | Executive Briefing | R5 Inteligência Digital',
      description: 'Executive briefing da R5 sobre tecnologia, dados, IA e sistemas inteligentes para hospitais e grupos hospitalares.',
      keywords: ['hospitais', 'grupos hospitalares', 'executive briefing', 'dados e IA em hospitais', 'sistemas inteligentes em saúde'],
      schemaType: 'WebPage',
      serviceType: 'Executive briefing sobre tecnologia para hospitais'
    },
    {
      match: (p) => p === '/insights/supermarkets',
      title: 'Supermarkets | Executive Briefing | R5 Inteligência Digital',
      description: 'Executive briefing da R5 sobre tecnologia, dados, IA e sistemas inteligentes para redes supermercadistas.',
      keywords: ['supermercados', 'grocery retail', 'executive briefing', 'dados e IA no varejo', 'sistemas inteligentes em supermercados'],
      schemaType: 'WebPage',
      serviceType: 'Executive briefing sobre tecnologia para supermercados'
    },
    {
      match: (p) => p.startsWith('/artigo/'),
      title: 'Artigo | R5 Inteligência Digital',
      description: 'Aprofunde-se em artigos e análises da R5 sobre tecnologia, inovação, dados, IA e estratégia digital.',
      keywords: ['artigo de tecnologia', 'análise de inovação', 'estratégia digital'],
      schemaType: 'Article'
    },
    {
      match: (p) => p === '/search',
      title: 'Busca | R5 Inteligência Digital',
      description: DEFAULT_DESCRIPTION,
      robots: 'noindex, nofollow'
    },
    {
      match: (p) => ['/admin', '/login', '/portal-cliente', '/design-system'].includes(p),
      title: 'Área Restrita | R5 Inteligência Digital',
      description: DEFAULT_DESCRIPTION,
      robots: 'noindex, nofollow'
    }
  ];

  const matched = routes.find((route) => route.match(pathname));
  if (!matched) {
    return {
      title: `Página não encontrada | ${SITE_NAME}`,
      description: 'O endereço acessado não corresponde a uma página válida do portal da R5 Inteligência Digital.',
      robots: 'noindex, nofollow',
      keywords: buildKeywordString(DEFAULT_KEYWORDS, ['404', 'página não encontrada']),
      schemaType: 'WebPage',
      serviceType: null,
      image: DEFAULT_IMAGE,
      type: 'website'
    };
  }

  return {
    title: matched.title || DEFAULT_TITLE,
    description: matched.description || DEFAULT_DESCRIPTION,
    robots: matched.robots || 'index, follow',
    keywords: buildKeywordString(DEFAULT_KEYWORDS, matched.keywords || []),
    schemaType: matched.schemaType || 'WebPage',
    serviceType: matched.serviceType || null,
    image: DEFAULT_IMAGE,
    type: 'website'
  };
};

const buildBreadcrumbJsonLd = (pathname) => {
  const segments = pathname.split('/').filter(Boolean);
  const itemListElement = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: `${SITE_URL}/`
    }
  ];

  let currentPath = '';
  segments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    itemListElement.push({
      '@type': 'ListItem',
      position: index + 2,
      name: decodeURIComponent(segment).replace(/-/g, ' '),
      item: `${SITE_URL}${currentPath}`
    });
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement
  };
};

const fetchJson = async (url, signal) => {
  const response = await fetch(url, { signal });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  return response.json();
};

const fetchDynamicSeo = async (pathname, signal) => {
  if (pathname === '/news') {
    const list = await fetchJson(`${API_URL}/news?channel=${encodeURIComponent(CONTENT_CHANNEL)}`, signal);
    const stories = Array.isArray(list) ? list.slice(0, 12) : [];
    if (stories.length > 0) {
      return {
        keywords: keywordListFromText(
          'notícias R5',
          'inteligência artificial',
          'transformação digital',
          stories.map((story) => [story?.title, story?.subtitle, story?.label]).flat()
        ),
        schemaType: 'CollectionPage',
        collectionItems: stories
          .filter((story) => story?.id && story?.title)
          .map((story, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            url: `${SITE_URL}/news/${story.id}`,
            name: story.title
          }))
      };
    }
  }

  const newsMatch = pathname.match(/^\/news\/(\d+)/);
  if (newsMatch && newsMatch[1]) {
    const targetId = String(newsMatch[1]);
    const list = await fetchJson(`${API_URL}/news?channel=${encodeURIComponent(CONTENT_CHANNEL)}`, signal);
    const story = Array.isArray(list)
      ? list.find((item) => String(item?.id) === targetId)
      : null;

    if (story) {
      const rawDescription = story.subtitle || stripHtml(story.content) || DEFAULT_DESCRIPTION;
      const description = truncate(rawDescription, 180);
      const image = normalizeAbsoluteUrl(story.image || firstGalleryImage(story.gallery)) || DEFAULT_IMAGE;
      const keywords = keywordListFromText(
        story.title,
        story.subtitle,
        story.label,
        story.category,
        Array.isArray(story.tags) ? story.tags : []
      );
      return {
        title: `${story.title} | ${SITE_NAME}`,
        description,
        image,
        type: 'article',
        keywords,
        schemaType: 'NewsArticle',
        article: {
          headline: story.title,
          description,
          image,
          datePublished: story.publishedAt || null,
          dateModified: story.updatedAt || story.publishedAt || null,
          authorName: story.author || 'Equipe R5',
          section: story.label || story.category || 'Notícias',
          keywords
        }
      };
    }
  }

  const articleMatch = pathname.match(/^\/artigo\/([^/]+)/);
  if (articleMatch && articleMatch[1]) {
    const slug = decodeURIComponent(articleMatch[1]);
    const article = await fetchJson(`${API_URL}/site-sections/${encodeURIComponent(slug)}?channel=${encodeURIComponent(CONTENT_CHANNEL)}`, signal);
    if (article) {
      const bodyText = stripHtml(article.body || article.description || article.content?.summary || '');
      const rawDescription = article.description || bodyText || DEFAULT_DESCRIPTION;
      const description = truncate(rawDescription, 180);
      const image = normalizeAbsoluteUrl(article.content?.image) || DEFAULT_IMAGE;
      const title = article.title ? `${article.title} | ${SITE_NAME}` : DEFAULT_TITLE;
      const keywords = keywordListFromText(
        article.title,
        article.description,
        article.content?.summary,
        article.content?.author
      );
      return {
        title,
        description,
        image,
        type: 'article',
        keywords,
        schemaType: 'Article',
        article: {
          headline: article.title || SITE_NAME,
          description,
          image,
          datePublished: article.createdAt || null,
          dateModified: article.updatedAt || article.createdAt || null,
          authorName: article.content?.author || 'Equipe R5',
          section: 'Artigos',
          keywords
        }
      };
    }
  }

  return null;
};

const applySeo = ({ pathname, seo }) => {
  const canonical = `${SITE_URL}${pathname === '/' ? '/' : pathname}`;
  const pageUrl = canonical;

  document.title = seo.title || DEFAULT_TITLE;

  upsertMeta('meta[name="description"]', {
    selectorAttrs: { name: 'description' },
    values: { content: seo.description || DEFAULT_DESCRIPTION }
  });

  upsertMeta('meta[name="robots"]', {
    selectorAttrs: { name: 'robots' },
    values: { content: seo.robots || 'index, follow' }
  });

  upsertMeta('meta[name="keywords"]', {
    selectorAttrs: { name: 'keywords' },
    values: { content: seo.keywords || buildKeywordString(DEFAULT_KEYWORDS) }
  });

  upsertLink('link[rel="canonical"]', {
    selectorAttrs: { rel: 'canonical' },
    values: { href: canonical }
  });

  upsertMeta('meta[property="og:title"]', {
    selectorAttrs: { property: 'og:title' },
    values: { content: seo.title || DEFAULT_TITLE }
  });

  upsertMeta('meta[property="og:description"]', {
    selectorAttrs: { property: 'og:description' },
    values: { content: seo.description || DEFAULT_DESCRIPTION }
  });

  upsertMeta('meta[property="og:type"]', {
    selectorAttrs: { property: 'og:type' },
    values: { content: seo.type || 'website' }
  });

  upsertMeta('meta[property="og:site_name"]', {
    selectorAttrs: { property: 'og:site_name' },
    values: { content: SITE_NAME }
  });

  upsertMeta('meta[property="og:locale"]', {
    selectorAttrs: { property: 'og:locale' },
    values: { content: 'pt_BR' }
  });

  upsertMeta('meta[property="og:url"]', {
    selectorAttrs: { property: 'og:url' },
    values: { content: pageUrl }
  });

  upsertMeta('meta[property="og:image"]', {
    selectorAttrs: { property: 'og:image' },
    values: { content: seo.image || DEFAULT_IMAGE }
  });

  upsertMeta('meta[property="og:image:width"]', {
    selectorAttrs: { property: 'og:image:width' },
    values: { content: DEFAULT_OG_IMAGE_WIDTH }
  });

  upsertMeta('meta[property="og:image:height"]', {
    selectorAttrs: { property: 'og:image:height' },
    values: { content: DEFAULT_OG_IMAGE_HEIGHT }
  });

  upsertMeta('meta[name="twitter:card"]', {
    selectorAttrs: { name: 'twitter:card' },
    values: { content: 'summary_large_image' }
  });

  upsertMeta('meta[name="twitter:title"]', {
    selectorAttrs: { name: 'twitter:title' },
    values: { content: seo.title || DEFAULT_TITLE }
  });

  upsertMeta('meta[name="twitter:description"]', {
    selectorAttrs: { name: 'twitter:description' },
    values: { content: seo.description || DEFAULT_DESCRIPTION }
  });

  upsertMeta('meta[name="twitter:image"]', {
    selectorAttrs: { name: 'twitter:image' },
    values: { content: seo.image || DEFAULT_IMAGE }
  });

  upsertJsonLd('r5-organization-jsonld', {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    description: DEFAULT_DESCRIPTION,
    logo: `${SITE_URL}/r5-icon-round-v3.png`,
    sameAs: SAME_AS_URLS,
    contactPoint: [{
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: 'contato@r5id.com.br',
      availableLanguage: ['pt-BR', 'en', 'es']
    }]
  });

  upsertJsonLd('r5-professional-service-jsonld', {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: SITE_NAME,
    url: SITE_URL,
    image: `${SITE_URL}/seo-share-r5id.png`,
    logo: `${SITE_URL}/r5-icon-round-v3.png`,
    description: seo.description || DEFAULT_DESCRIPTION,
    areaServed: PROFESSIONAL_SERVICE_AREAS,
    sameAs: SAME_AS_URLS,
    serviceType: seo.serviceType || 'Consultoria em transformação digital, inteligência artificial e inovação aplicada',
    knowsAbout: CORE_SERVICE_TOPICS,
    contactPoint: [{
      '@type': 'ContactPoint',
      contactType: 'sales',
      email: 'contato@r5id.com.br',
      availableLanguage: ['pt-BR', 'en', 'es']
    }]
  });

  upsertJsonLd('r5-website-jsonld', {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE_URL}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string'
    }
  });

  upsertJsonLd('r5-breadcrumb-jsonld', buildBreadcrumbJsonLd(pathname));

  upsertJsonLd('r5-webpage-jsonld', {
    '@context': 'https://schema.org',
    '@type': seo.schemaType || 'WebPage',
    name: seo.title || DEFAULT_TITLE,
    description: seo.description || DEFAULT_DESCRIPTION,
    url: pageUrl,
    inLanguage: 'pt-BR',
    isPartOf: {
      '@type': 'WebSite',
      name: SITE_NAME,
      url: SITE_URL
    },
    about: seo.serviceType
      ? {
          '@type': 'Thing',
          name: seo.serviceType
        }
      : undefined,
    primaryImageOfPage: {
      '@type': 'ImageObject',
      url: seo.image || DEFAULT_IMAGE
    }
  });

  if (seo.schemaType === 'CollectionPage' && Array.isArray(seo.collectionItems) && seo.collectionItems.length > 0) {
    upsertJsonLd('r5-collection-jsonld', {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: seo.title || 'Conteúdos R5',
      itemListElement: seo.collectionItems
    });
  } else {
    removeJsonLd('r5-collection-jsonld');
  }

  if (seo.type === 'article' && seo.article) {
    upsertMeta('meta[property="article:published_time"]', {
      selectorAttrs: { property: 'article:published_time' },
      values: { content: seo.article.datePublished || '' }
    });

    upsertMeta('meta[property="article:modified_time"]', {
      selectorAttrs: { property: 'article:modified_time' },
      values: { content: seo.article.dateModified || seo.article.datePublished || '' }
    });

    upsertJsonLd('r5-article-jsonld', {
      '@context': 'https://schema.org',
      '@type': seo.schemaType || 'Article',
      headline: seo.article.headline,
      description: seo.article.description,
      image: seo.article.image,
      datePublished: seo.article.datePublished,
      dateModified: seo.article.dateModified,
      articleSection: seo.article.section || undefined,
      keywords: seo.article.keywords || seo.keywords || undefined,
      author: {
        '@type': 'Person',
        name: seo.article.authorName || 'Equipe R5'
      },
      publisher: {
        '@type': 'Organization',
        name: SITE_NAME,
        logo: {
          '@type': 'ImageObject',
          url: `${SITE_URL}/r5-icon-round-v3.png`
        }
      },
      mainEntityOfPage: pageUrl
    });
  } else {
    removeJsonLd('r5-article-jsonld');
  }
};

const SeoManager = () => {
  const location = useLocation();

  useEffect(() => {
    const pathname = location.pathname || '/';
    const staticSeo = resolveSeoByPath(pathname);
    applySeo({ pathname, seo: staticSeo });

    const controller = new AbortController();

    fetchDynamicSeo(pathname, controller.signal)
      .then((dynamicSeo) => {
        if (dynamicSeo) {
          applySeo({ pathname, seo: { ...staticSeo, ...dynamicSeo } });
        }
      })
      .catch(() => {
        // Keep static SEO fallback when dynamic fetch fails.
      });

    return () => controller.abort();
  }, [location.pathname, location.search]);

  return null;
};

export default SeoManager;
